/**
 * Converts camelCase or snake_case to title case (e.g. "fooBar" → "Foo Bar").
 * Inline implementation to avoid lodash ESM resolution differences between client and server.
 */
function startCase(str) {
    return str
        .replace(/([A-Z])/g, ' $1')
        .replace(/[-_]+/g, ' ')
        .trim()
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}
/**
 * Registered resource types for the audit system.
 * Use these constants instead of raw strings to prevent typos and enable autocomplete.
 * Add a new entry here whenever you onboard a new auditable resource.
 */
export const AuditResourceType = {
    LegislativeItem: 'legislative_item',
    MeetingBody: 'meeting_body',
    Role: 'role',
    MeetingBodyRole: 'meeting_body_role',
    UserRoleAssignment: 'user_role_assignment',
};
/**
 * Per-resource label overrides for audit fields where auto-generation from camelCase isn't ideal.
 * Keys are resource types, values are field → label maps.
 */
const AUDIT_FIELD_LABELS = {
    legislative_item: {
        legislativeTypeId: 'Item Type',
        legislativeStatusId: 'Status',
        isPubliclyReleased: 'Publicly Released',
        departmentId: 'Department',
        // Kept alongside `staffContacts` so audit rows written before items supported
        // multiple staff contacts still render a label.
        staffContactId: 'Staff Contact',
        staffContacts: 'Staff Contacts',
        recommendedActionId: 'Recommended Action',
        targetMeetingId: 'Target Meeting',
        workflowTemplateId: 'Workflow Template',
        executiveSession: 'Executive Session',
        requestingEmergency: 'Requesting Emergency',
        requestingConsent: 'Requesting Consent',
        publicHearing: 'Public Hearing',
        securityLevel: 'Security Level',
        sponsors: 'Sponsors',
        meetingBodies: 'Meeting Bodies',
        documents: 'Documents',
        attachmentTypes: 'Attachment Types',
        attachments: 'Attachments',
    },
    role: {
        isSystemAdmin: 'System Admin',
        meetingBodyRoles: 'Meeting Body Roles',
    },
    meeting_body_role: {
        isMinutesAdmin: 'Minutes & Record Management',
        isMeetingAdmin: 'Meeting Operations',
        isAgendaAdmin: 'Agenda & Item Management',
        isLegislativeItemsAdmin: 'Legislative Items Management',
        isBoardAdmin: 'Board & Commission Admin',
        isBoardViewer: 'Board & Commission Viewer',
        isGlobalViewer: 'Global Viewer',
    },
    user_role_assignment: {
        roles: 'Roles',
    },
    meeting_body: {
        adminId: 'Admin',
        contactId: 'Contact',
        locationId: 'Location',
        contactPhoneId: 'Contact Phone',
        seatApplicationTemplateId: 'Seat Application Template',
        seatApplicationWorkflowId: 'Seat Application Workflow',
        recurringInterval: 'Recurring Interval',
        recurringDay: 'Recurring Day',
        recurringTime: 'Recurring Time',
        isActive: 'Active',
        isPrivate: 'Private',
        logoUrl: 'Logo',
        contactName: 'Contact Name',
        contactEmail: 'Contact Email',
        categories: 'Categories',
        agendaTemplates: 'Agenda Templates',
        seats: 'Seats',
        staffMembers: 'Staff Members',
        userId: 'User',
        meetingBody: 'Meeting Body',
        termLength: 'Term Length (Months)',
        termLimit: 'Term Limit',
        termStart: 'Term Start Date',
        allowApplications: 'Allow Applications',
        appointedBy: 'Appointed By',
    },
};
/**
 * Get the display label for an audit field.
 * Checks per-resource overrides first, then falls back to auto-generating from camelCase.
 */
export function getAuditFieldLabel(resourceType, field) {
    return AUDIT_FIELD_LABELS[resourceType]?.[field] ?? startCase(field);
}
